"use client";

import React, { useState, useEffect, useCallback } from "react";
import CloseIcon from "./CloseIcon";

export interface ErrorToastProps {
  /**
   * The error object or message to display
   */
  error: Error | { message: string } | string | null;
  /**
   * Callback function when the toast is closed
   */
  onClose?: () => void;
  /**
   * Auto-close duration in milliseconds (0 or undefined to disable)
   */
  autoCloseDuration?: number;
}

const ErrorToast: React.FC<ErrorToastProps> = ({
  error,
  onClose,
  autoCloseDuration,
}) => {
  const [isVisible, setIsVisible] = useState(!!error);

  // Convert error to a standard format
  const errorMessage = typeof error === 'string'
    ? error
    : error?.message || 'An error occurred';

  const closeError = useCallback(() => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const handleEscapeKey = useCallback((event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      closeError();
    }
  }, [closeError]);

  // Handle auto-close if duration is provided
  useEffect(() => {
    if (autoCloseDuration && autoCloseDuration > 0) {
      const timer = setTimeout(() => {
        closeError();
      }, autoCloseDuration);

      return () => clearTimeout(timer);
    }
  }, [autoCloseDuration, closeError]);

  // Update visibility when error changes
  useEffect(() => {
    setIsVisible(!!error);
  }, [error]);

  if (!isVisible || !error) {
    return null;
  }

  return (
    <section
      role="alert"
      aria-live="assertive"
      className="flex fixed top-5 right-5 gap-3 items-center px-5 py-4 text-white rounded-lg bg-stone-700 max-w-[400px] shadow-[rgba(0,0,0,0.35)_0px_4px_15px_2px] z-[1000] max-sm:inset-x-5 max-sm:max-w-none"
      onKeyDown={handleEscapeKey}
    >
      <svg className="pt-2 mx-auto mb-4 text-gray-400 w-12 h-12 dark:text-gray-200" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
      </svg>
      <p className="flex-1 text-sm leading-normal">{errorMessage}</p>
      <button
        className="p-1 text-white opacity-70 transition-opacity cursor-pointer border-[none] duration-[0.2s] hover:opacity-100 focus:opacity-100 focus:outline-none"
        aria-label="Close error message"
        onClick={closeError}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            closeError();
          }
        }}
      >
        <CloseIcon />
      </button>
    </section>
  );
};

export default ErrorToast;
